import { Dispatch, SetStateAction } from 'react'
import {
  FieldReportForm,
  FieldReportFormData,
} from '@/components/forms/FieldReportFormContainer/FieldReportForm'
import {
  useMutationWrapper,
  usePatchFieldReport,
  useUploadMedia,
} from '@/hooks'
import { VisitLog as VisitLogType, ModelsWithMedia, MediaType } from '@/types'

export type FieldReportProps = {
  data: VisitLogType
  fieldReportId: number
  setUpdatedVisitLog: Dispatch<SetStateAction<VisitLogType>>
  onSuccessUpdate: () => void
}

export const FieldReportFormContainer = ({
  data,
  fieldReportId,
  setUpdatedVisitLog,
  onSuccessUpdate,
}: FieldReportProps): JSX.Element => {
  const {
    mutateAsync: updateFieldReport,
    isLoading: mutateFieldReportInProcess,
    error,
  } = useMutationWrapper(
    usePatchFieldReport.bind(null, {
      id: fieldReportId,
    })
  )

  const { uploadMedia, isLoading: isMediaUploading } = useUploadMedia({
    modelKey: ModelsWithMedia.fieldReports,
  })

  const handleFieldReportSubmit = async ({
    field_impression,
    round_impression,
    photos,
  }: FieldReportFormData) => {
    if (data.research_visit_event) {
      if (photos?.chosenToUpload || photos?.stale) {
        await uploadMedia({
          media: photos,
          entityId: fieldReportId,
        })
      }

      const fieldReportData = {
        field_impression,
        round_impression,
        research_visit_event_id: data.research_visit_event.id,
      }

      await updateFieldReport(fieldReportData, {
        successMessageKey: 'dataValidation:notifications.fieldReportUpdated',
        onSuccess: (fieldReport) => {
          setUpdatedVisitLog((visitLog) =>
            visitLog.research_visit_event
              ? {
                  ...visitLog,
                  research_visit_event: {
                    ...visitLog.research_visit_event,
                    field_report: {
                      ...fieldReport,
                    },
                  },
                }
              : {
                  ...visitLog,
                }
          )

          onSuccessUpdate()
        },
      })
    }
  }

  const defaultValues = {
    field_impression:
      data.research_visit_event?.field_report?.field_impression ?? '',
    round_impression:
      data.research_visit_event?.field_report?.round_impression ?? '',
    auto_corrected:
      data.research_visit_event?.field_report?.auto_corrected ?? false,
    original_field_impression:
      data.research_visit_event?.field_report?.original_field_impression ?? '',
    original_round_impression:
      data.research_visit_event?.field_report?.original_round_impression ?? '',
    photos: {
      chosenToUpload: [],
      uploadedToApi: data?.research_visit_event?.field_report?.photos ?? [],
      stale: [],
      mediaType: MediaType.photos,
    },
  }

  return (
    <FieldReportForm
      defaultValues={defaultValues}
      onSubmit={handleFieldReportSubmit}
      isLoading={mutateFieldReportInProcess || isMediaUploading}
      apiErrorData={error?.data}
    />
  )
}
